/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useDispatch, useSelector, useStore } from "react-redux";
import React, { useCallback, useEffect, useRef, useState } from "react";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import { useAuth0 } from "@auth0/auth0-react";
import { actions } from "../../store/modelActionCreators";
import { gridActions } from "../../store/gridActions";
import { DateRange, Picker } from "@risk-first/ui-picker";

import {
  DependencyData,
  CheckByFilterRequest,
  RestorePendingDependencies,
  DailyValuationKickoffRequest,
  ForceMode,
} from "../../store/types";
import { DailyValuationKickoffDialog } from "./DailyValuationKickoffDialog";
import { ForceGroupDialog } from "./ForceGroupDialog";
import { getGroupForceBlockedReason, hasBlankGroupKey, normaliseGroupKeys, toGroupPath } from "./groupForce";
import {
  filterValidRows,
  createForceClientPayload,
  createForceClientAgentPayload,
  createRestoreBySchemePayload,
  createRestoreByClientPayload,
  createRestoreByClientAgentPayload,
  createRestoreByEnvPayload,
  createRestoreAllPayload,
  countRowsMissingMetaData,
  RestoreType,
} from "../../store/dependencyHandlers";
import { RootState } from "../../store";

import {
  ColDef,
  Column,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideGetRowsParams,
  IRowNode,
  ITooltipParams,
  MenuItemDef
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import * as formatter from "./valueFormatters";
import { DependencyStatus } from "../../store/types";
import { onCacheBlockLoaded, getLastWeekDate, getLast10YearDate } from "./gridUtils";
import { buildContextMenuItems } from "./contextMenuBuilder";
import { DependencyNameFilter } from "./DependencyNameFilter";
import { StatusCellRenderer } from "./StatusCellRenderer";
import { StatusCountsTooltip } from "./StatusCountsTooltip";
import { StatusLegendButton } from "./StatusLegendButton";
import { statusTooltipText } from "./statusProgress";
import { statusCountsActions } from "../../store/statusCountsSlice";
import { withTheme } from "@emotion/react";
import {
  SnackbarNotification,
  enqueueSnackbar as enqueueSnackbarAction,
} from "../../store/snackbarActions";
import jwt_decode from "jwt-decode";
import * as signalR from "@microsoft/signalr";
import axios from "axios";
import {
  ActionsContainer,
  ButtonsContainer,
  LegendRow,
  GridButton,
} from "./DependenciesGrid.style";
import moment from "moment";

const UPDATE_DEPENDENCIES_SCOPE = "update:dependencies";
const RESTORE_DEPENDENCIES_SCOPE = "restore:dependencies";
const RELEASE_DEPENDENCIES_SCOPE = "release:dependencies";
const RERUN_DEPENDENCIES_SCOPE = "rerun:dependencies";
const cozonacApiUrl = process.env.REACT_APP_COZONAC_API_URL;
const innitialPickerDates = { from: getLastWeekDate(), to: new Date() }

type DecodedJwt = {
  permissions: Array<string>;
};

export const DependenciesGrid = withTheme(() => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [gridColumnApi, setGridColumnApi] = useState<GridApi | null>(null);
  // Stable ref to gridApi for custom components (e.g. DependencyNameFilter) whose
  // params.api inside ag-grid v31 does not reflect other columns' applied filters.
  const gridApiRef = useRef<GridApi | null>(null);
  // Guards against state updates from onCacheBlockLoaded's polling callback firing
  // after the component has unmounted (e.g. route change before the first cache
  // block finishes loading).
  const isMountedRef = useRef(true);
  // Tracks the two independent async gates the grid goes through on load: saved
  // column/filter state + datasource wiring (below), and the first cache block
  // render (onGridReady). Exposed via data-grid-ready so e2e tests can wait for a
  // real readiness signal instead of racing AG Grid's internal render timing.
  const [gridStateApplied, setGridStateApplied] = useState(false);
  const [gridFirstRenderDone, setGridFirstRenderDone] = useState(false);
  const [rowSelected, setRowSelected] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [datepickerDates, setDatepickersDate] = useState<DateRange | undefined>(innitialPickerDates);
  const [canRestore, setCanRestore] = useState(false);
  const [canRelease, setCanRelease] = useState(false);
  const [canRerun, setCanRerun] = useState(false);
  const [kickoffDialogOpen, setKickoffDialogOpen] = useState(false);
  const [rowSelectedLDV, setRowSelectedLDV] = useState(false);
  const [rowSelectedADV, setRowSelectedADV] = useState(false);
  const [rowSelectedADVDep, setRowSelectedADVDep] = useState(false);
  const [rowSelectedDM, setRowSelectedDM] = useState(false);
  const [rowsSelectedMissingClient, setRowsSelectedMissingClient] = useState(0);
  const [rowsSelectedMissingClientAgent, setRowsSelectedMissingClientAgent] = useState(0);
  const [forceGroup, setForceGroup] = useState<{
    mode: ForceMode;
    criteria: CheckByFilterRequest;
    groupPath: string[];
  } | null>(null);

  const dispatch = useDispatch<any>();
  const store = useStore<RootState>();
  const auth0Context = useAuth0();
  const currentViewInfo = useSelector((state: RootState) => state.grid);
  const model = useSelector((state: RootState) => state.model);

  /**
   * Extracts group keys from a node by traversing up the parent chain.
   * Returns an object with field names as keys and group values as values.
   * e.g., { AppName: "PFaroe", Type: "Liability DV", ValuationDate: "2026-03-09" }
   */
  const getGroupKeysFromNode = (node: IRowNode): Record<string, string> => {
    const keys: Record<string, string> = {};
    let current: IRowNode | null = node;
    while (current) {
      if (current.field && current.key !== undefined && current.key !== null) {
        keys[current.field] = String(current.key);
      }
      current = current.parent as IRowNode | null;
    }
    return keys;
  };

  /**
   * Gets the current filter model from the grid, used for filter-based actions.
   */
  const getCurrentFilterCriteria = (): Partial<CheckByFilterRequest> => {
    if (!gridApi) return {};
    const filterModel = gridApi.getFilterModel() ?? {};
    const criteria: Partial<CheckByFilterRequest> = {};

    // Extract status filter
    if (filterModel.status?.values) {
      criteria.status = filterModel.status.values;
    }

    if (filterModel.AppName?.filter) {
      criteria.appName = filterModel.AppName.filter;
    }
    if (filterModel.Type?.filter) {
      criteria.type = filterModel.Type.filter;
    }
    if (filterModel.Client?.filter) {
      criteria.client = filterModel.Client.filter;
    }
    if (filterModel.ClientAgent?.filter) {
      criteria.clientAgent = filterModel.ClientAgent.filter;
    }
    if (filterModel.SchemeId?.filter != null) {
      criteria.schemeId = String(filterModel.SchemeId.filter);
    }
    if (filterModel.Scheme?.filter) {
      criteria.scheme = filterModel.Scheme.filter;
    }
    if (filterModel.Location?.filter) {
      criteria.location = filterModel.Location.filter;
    }
    if (filterModel.DependencyType?.filter) {
      criteria.dependencyType = filterModel.DependencyType.filter;
    }
    if (filterModel.dependencyName?.values) {
      criteria.dependencyName = filterModel.dependencyName.values;
    }

    // Extract ValuationDate filter
    if (filterModel.ValuationDate) {
      if (filterModel.ValuationDate.type === 'inRange') {
        criteria.valuationDateFrom = filterModel.ValuationDate.dateFrom;
        criteria.valuationDateTo = filterModel.ValuationDate.dateTo;
      } else if (filterModel.ValuationDate.dateFrom) {
        criteria.valuationDate = filterModel.ValuationDate.dateFrom;
      }
    }

    return criteria;
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const getUserClaims = async () => {
      const { getAccessTokenSilently } = auth0Context;

      const token = await getAccessTokenSilently();
      const decoded = jwt_decode(token) as DecodedJwt;
      if (decoded.permissions.includes(UPDATE_DEPENDENCIES_SCOPE)) {
        setCanUpdate(true);
      }

      if (decoded.permissions.includes(RESTORE_DEPENDENCIES_SCOPE)) {
        setCanRestore(true);
      }

      if (decoded.permissions.includes(RELEASE_DEPENDENCIES_SCOPE)) {
        setCanRelease(true);
      }

      if (decoded.permissions.includes(RERUN_DEPENDENCIES_SCOPE)) {
        setCanRerun(true);
      }
    };

    getUserClaims();

    //setupSignalRConnection();
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setupSignalRConnection = async () => {
    function onConnected(connection: signalR.HubConnection) {
      console.log("connection started");
      connection.send("broadcastMessage", "Connected");
    }

    function bindConnectionMessage(connection: signalR.HubConnection) {
      const messageCallback = function (message: string) {
        if (!message) return;
        // deal with the message
        console.log("message received:" + message);
      };
      // Create a function that the hub can call to broadcast messages.
      connection.on("SendMessageToClient", messageCallback);
    }

    const { getAccessTokenSilently } = auth0Context;

    try {
      const token = await getAccessTokenSilently();

      const resp = await axios.get(
        `${cozonacApiUrl}/api/hub/negotiate/dependencies`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(resp.data.url, {
          accessTokenFactory: () => resp.data.accessToken,
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .configureLogging(signalR.LogLevel.Information)
        .withAutomaticReconnect()
        .build();

      bindConnectionMessage(connection);
      connection
        .start()
        .then(function () {
          onConnected(connection);
        })
        .catch(function (error) {
          console.error(error.message);
        });
    } catch (error) {
      if (error instanceof Error) {
        console.log(error.message);
      } else {
        console.log("Unexpected error", error);
      }
    }
  };

  const enqueueSnackbar = (notification: SnackbarNotification) =>
    dispatch(enqueueSnackbarAction(notification));

  // Every full reload goes through here so the progress-marker counts are refetched too.
  const purgeGrid = (): void => {
    dispatch(statusCountsActions.clear());
    gridApi?.refreshServerSide({ purge: true });
  };

  const defaultColDef: ColDef = {
    minWidth: 50,
    enableValue: true,
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: false,
    enableCellChangeFlash: true,
    suppressMenu: false,
    filterParams: {
      buttons: ["apply", "reset"],
      closeOnApply: true,
      suppressAndOrCondition: true,
    }
  };

  const gridOptions: GridOptions = {
    alwaysShowHorizontalScroll: true,
    rowGroupPanelShow: "always",
    groupDisplayType: "groupRows",
    groupRowRendererParams: {
      suppressCount: true,
      innerRenderer: (params: any) => {
        const value = params.valueFormatted ?? params.value ?? '';
        const count = params.node?.data?.childCount;
        if (count != null) {
          return (
            <span>
              {value}
              <span style={{ marginLeft: '20px', color: '#888', fontSize: '0.85em' }}>{count} rows</span>
            </span>
          );
        }
        return String(value);
      },
    },
    groupSelectsChildren: true,
    groupSelectsFiltered: true,
    groupDefaultExpanded: 0,
    isRowSelectable: (node: IRowNode) => {
      // Allow group rows to be selectable for group-level actions
      if (node.group) return true;
      return node.data
        ? node.data.status === DependencyStatus[0] ||
            node.data.status === DependencyStatus[2] ||
            node.data.status === DependencyStatus[3] ||
            node.data.status === 0
        : false;
    },
    rowMultiSelectWithClick: true,
    rowSelection: "multiple",
    sideBar: {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressPivotMode: true,
          },
        },
        "filters",
      ],
    },
    rowModelType: "serverSide",
    suppressServerSideInfiniteScroll: false,
    blockLoadDebounceMillis: 200,
    cacheBlockSize: 50,
    maxBlocksInCache: 10,
    serverSideFilterAllLevels: true,
    serverSideFilterOnServer: true,
    tooltipShowDelay: 300,
    tooltipHideDelay: 15000,
    columnTypes: {
      groupColumn: {
        enableRowGroup: true,
      },
      textColumn: {
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: [
            "equals",
            "notEqual",
            "contains",
            "notContains",
            "startsWith",
            "endsWith",
            "empty",
          ],
          suppressAndOrCondition: true,
          defaultOption: "contains",
        },
      },
      numberColumn: {
        filter: "agNumberColumnFilter",
        filterParams: {
          filterOptions: [
            "equals",
            "notEqual",
            "lessThan",
            "lessThanOrEqual",
            "greaterThan",
            "greaterThanOrEqual",
            "inRange",
            "empty",
          ],
          suppressAndOrCondition: true,
          defaultOption: "equals",
        },
      },
      dateColumn: {
        filter: "agDateColumnFilter",
        valueFormatter: formatter.formatDate,
        filterParams: {
          comparator: formatter.dateComparator,
        },
      },
      dateTimeColumn: {
        filter: "agDateColumnFilter",
        valueFormatter: formatter.formatDateTime,
        filterParams: {
          comparator: formatter.dateComparator,
        },
      },
      statusColumn: {
        filter: "agSetColumnFilter",
        filterParams: {
          values: Object.keys(DependencyStatus).filter((k) => isNaN(Number(k))),
        },
        cellRenderer: "statusCellRenderer",
        tooltipComponent: "statusCountsTooltip",
        // Read at hover time (no subscription); undefined means no tooltip.
        tooltipValueGetter: (params: ITooltipParams) => {
          const { statusCounts } = store.getState();
          return statusTooltipText(params.data, statusCounts.byKey[params.data?.partitionKey], statusCounts.retentionDays);
        },
      },
    },

    onFilterChanged: (event) => {
      const currentFilters = event.api?.getFilterModel() ?? {};

      const fromDateString = moment(datepickerDates?.from).format("YYYY-MM-DD HH:mm:ss");
      const toDateString = moment(datepickerDates?.to).format("YYYY-MM-DD HH:mm:ss");

      if(currentFilters["ValuationDate"])
      {
        if(currentFilters["ValuationDate"].type === 'equals'){
          if(currentFilters["ValuationDate"].dateFrom === fromDateString) return;
          setDatepickersDate( { from: new Date(currentFilters["ValuationDate"].dateFrom), to: new Date(currentFilters["ValuationDate"].dateFrom) });
        }
        else if(currentFilters["ValuationDate"].type === 'lessThan'){
          if(currentFilters["ValuationDate"].dateFrom === toDateString) return;
          setDatepickersDate({ from: getLast10YearDate(), to: new Date(currentFilters["ValuationDate"].dateFrom) });
        }
        else if(currentFilters["ValuationDate"].type === 'greaterThan'){
          if(currentFilters["ValuationDate"].dateFrom === fromDateString) return;
          setDatepickersDate( { from: new Date(currentFilters["ValuationDate"].dateFrom), to: new Date() });
        }
        else if(currentFilters["ValuationDate"].type === 'inRange') {
          if(currentFilters["ValuationDate"].dateFrom === fromDateString && currentFilters["ValuationDate"].dateTo === toDateString) return;
          setDatepickersDate( { from: new Date(currentFilters["ValuationDate"].dateFrom), to: new Date(currentFilters["ValuationDate"].dateTo) });
        }
        else {
          setDatepickersDate(undefined);
        }
      }

    }
  };

  const getContextMenuItems = (params: { node?: IRowNode | null }): (string | MenuItemDef)[] =>
    buildContextMenuItems(
      params,
      {
        canUpdate, canRestore, rowSelected, rowSelectedLDV, rowSelectedADV, rowSelectedADVDep, rowSelectedDM,
        rowsSelectedMissingClient, rowsSelectedMissingClientAgent,
        groupForceBlockedReason: params.node?.group
          ? getGroupForceBlockedReason(
            getGroupKeysFromNode(params.node),
            gridApi?.getFilterModel() ?? {},
            hasBlankGroupKey(params.node)
          )
          : undefined,
      },
      {
        getGroupKeysFromNode,
        onCheckIsReadyForGroup,
        onForceClientForGroup: (keys) => openForceGroupDialog('Client', keys),
        onForceClientAgentForGroup: (keys) => openForceGroupDialog('ClientAgent', keys),
        onRestoreGroupByScheme, onRestoreGroupByClient, onRestoreGroupByClientAgent,
        onCheckExistsClick: () => { void onCheckExistsClick(); },
        onAbandonClick: () => { void onAbandonClick(); },
        onForceClientClick: () => { void onForceClientClick(); },
        onForceClientAgentClick: () => { void onForceClientAgentClick(); },
        onRestoreSchemePendingDependenciesClick,
        onRestoreClientPendingDependenciesClick,
        onRestoreClientAgentPendingDependenciesClick,
        onRestoreEnvPendingDependenciesClick,
        onExportGroupCsvClick,
        onExportCsvClick: () => { void onExportCsvClick(); },
      }
    );

  // Tracks whether we've done the one-time grid initialisation (column state + datasource).
  // columnDefs arrive asynchronously from the API, so we cannot initialise in onGridReady.
  const gridInitialized = useRef(false);

  // Once both the grid API and column definitions are available, apply the saved column
  // state, set the filter model, and set the datasource — all before the first SSRM
  // request fires so row groups are correct on the very first load.
  //
  // When there IS saved column state: wait for columnDefinitions to be pre-populated
  // (from cache in store/index.ts) so applyColumnState sets row groups before the
  // first fetch, avoiding a double load.
  //
  // When there is NO saved column state (first-time user, cleared cache): initialise
  // immediately once gridApi is ready — there are no column definitions to wait for.
  useEffect(() => {
    if (!gridApi || gridInitialized.current) return;
    // If there is saved column state but column definitions aren't ready yet, wait.
    if (currentViewInfo.columnState?.length && !model.columnDefinitions.length) return;

    gridInitialized.current = true;

    const savedColumnState = currentViewInfo.columnState;
    if (savedColumnState?.length) {
      gridApi.applyColumnState({ state: savedColumnState });
    }

    const filterState = currentViewInfo.filterModel;
    if (filterState) {
      gridApi.setFilterModel(filterState);
    }

    gridApi.setGridOption('serverSideDatasource', {
      getRows(params: IServerSideGetRowsParams) {
        dispatch(actions.fetchDependenciesDatasource(auth0Context, params));
      },
    });

    setGridStateApplied(true);
  }, [gridApi, model.columnDefinitions]);

  const onGridReady = (event: GridReadyEvent): void => {
    setGridApi(event.api);
    setGridColumnApi(event.api);
    gridApiRef.current = event.api;

    const columnState = { state: currentViewInfo.columnState };

    onCacheBlockLoaded(event, () => {
      if (!isMountedRef.current) return;

      if (!columnState.state) {
        event.api.autoSizeAllColumns();
      }

      const columnGroupState = currentViewInfo.columnGroupState;
      if (columnGroupState) {
        event.api.setColumnGroupState(columnGroupState);
      }

      setGridFirstRenderDone(true);
    });
  };

  const onSaveCurrentViewClick = (): void => {
    if (!gridColumnApi) return;
    const columnState = gridColumnApi.getColumnState();
    const columnGroupState = gridColumnApi.getColumnGroupState();
    const filterModel = gridApi?.getFilterModel() ?? {};

    localStorage.setItem("cache", JSON.stringify({
      sortModel: currentViewInfo.sortModel,
      filterModel,
      columnState,
      columnGroupState,
      isPivotMode: currentViewInfo.isPivotMode,
    }));

    // Keep Redux in sync for the current session.
    dispatch(gridActions.saveGridColumnState(columnState));
    dispatch(gridActions.saveGridColumnGroupState(columnGroupState));
    dispatch(gridActions.saveGridFilterModel(filterModel));
    enqueueSnackbar({
      message: "Successfully saved to local storage",
      key: new Date().getTime() + Math.random(),
      options: {
        key: new Date().getTime() + Math.random(),
        variant: "success",
      },
    });
  };

  const onCheckExistsClick = async (): Promise<void> => {
    if (!gridApi) return;
    const selectedRows = filterValidRows(gridApi.getSelectedRows() as DependencyData[]);
    if (selectedRows.length > 0) {
      // Pass full dependency objects to include routing info (AppName, ClientAgentId)
      await dispatch(actions.checkDependencyExists(auth0Context, selectedRows));
      gridApi.deselectAll();
      purgeGrid();
    }
  };

  const onAbandonClick = async (): Promise<void> => {
    if (!gridApi) return;
    const selectedRows = filterValidRows(gridApi.getSelectedRows() as DependencyData[]);
    if (selectedRows.length > 0) {
      await dispatch(actions.abandonDependencies(auth0Context, selectedRows));
      gridApi.deselectAll();
      purgeGrid();
    }
  };

  const onRefreshClick = async (): Promise<void> => {
    if (!gridApi) return;
    purgeGrid();
  };

  const onRowSelected = async (): Promise<void> => {
    if (!gridApi) return;
    const selectedRows = filterValidRows(gridApi.getSelectedRows() as DependencyData[]);
    setRowSelected(selectedRows.length > 0);
    setRowSelectedLDV(selectedRows.some((e: any) => e.Type === 'Liability DV') && selectedRows.length > 0);
    setRowSelectedADV(selectedRows.some((e: any) => e.Type === 'Asset DV') && selectedRows.length > 0);
    setRowSelectedDM(selectedRows.some((e: any) => e.Type === 'Daily Metric') && selectedRows.length > 0);
    setRowSelectedADVDep(selectedRows.some((e: any) => e.Type === 'Daily Metric' && e.DependencyType === 'AssetDV') && selectedRows.length > 0);
    setRowsSelectedMissingClient(countRowsMissingMetaData(selectedRows, 'Client'));
    setRowsSelectedMissingClientAgent(countRowsMissingMetaData(selectedRows, 'ClientAgent'));
  };

  const onForceClientClick = async (): Promise<void> => {
    if (!gridApi) return;
    const selectedRows = filterValidRows(gridApi.getSelectedRows() as DependencyData[]);
    if (selectedRows.length > 0) {
      const payload = createForceClientPayload(selectedRows);
      await dispatch(actions.forceClientDependency(auth0Context, payload));
      gridApi.deselectAll();
      purgeGrid();
    }
  };

  const onForceClientAgentClick = async (): Promise<void> => {
    if (!gridApi) return;
    const selectedRows = filterValidRows(gridApi.getSelectedRows() as DependencyData[]);
    if (selectedRows.length > 0) {
      const payload = createForceClientAgentPayload(selectedRows);
      await dispatch(actions.forceClientAgentDependency(auth0Context, payload));
      gridApi.deselectAll();
      purgeGrid();
    }
  };

  const onRestoreSchemePendingDependenciesClick = async (restoreType: RestoreType): Promise<void> =>{
    if (!gridApi) return;

    const selectedRows = filterValidRows(gridApi.getSelectedRows() as DependencyData[]);
    if (selectedRows.length > 0) {
      const payload = createRestoreBySchemePayload(selectedRows, restoreType);
      await dispatch(actions.restorePendingDependencies(auth0Context, payload));
      gridApi.deselectAll();
    }
  }

  const onRestoreClientPendingDependenciesClick = async (restoreType: RestoreType): Promise<void> => {
    if (!gridApi) return;

    const selectedRows = filterValidRows(gridApi.getSelectedRows() as DependencyData[]);
    if (selectedRows.length > 0) {
      const payload = createRestoreByClientPayload(selectedRows, restoreType);
      await dispatch(actions.restorePendingDependencies(auth0Context, payload));
      gridApi.deselectAll();
    }
  }

  const onRestoreClientAgentPendingDependenciesClick = async (restoreType: RestoreType): Promise<void> => {
    if (!gridApi) return;

    const selectedRows = filterValidRows(gridApi.getSelectedRows() as DependencyData[]);
    if (selectedRows.length > 0) {
      const payload = createRestoreByClientAgentPayload(selectedRows, restoreType);
      await dispatch(actions.restorePendingDependencies(auth0Context, payload));
      gridApi.deselectAll();
    }
  }

  const onRestoreEnvPendingDependenciesClick = async (restoreType: RestoreType): Promise<void> => {
    if (!gridApi) return;

    if(!rowSelected)
    {
        alert('Please select at least one `Pending` dependency.');
        return;
    }

    const selectedRows = filterValidRows(gridApi.getSelectedRows() as DependencyData[]);
    if (selectedRows.length > 0) {
      const payload = createRestoreByEnvPayload(selectedRows, restoreType);
      await dispatch(actions.restorePendingDependencies(auth0Context, payload));
      gridApi.deselectAll();
    }
  };

  const onRestoreAllPendingDependenciesClick = async (): Promise<void> => {
    const payload = createRestoreAllPayload();
    await dispatch(actions.restorePendingDependencies(auth0Context, payload));
  };

  const onDailyValuationKickoffConfirm = async (
    request: DailyValuationKickoffRequest
  ): Promise<void> => {
    setKickoffDialogOpen(false);
    await dispatch(actions.dailyValuationKickoff(auth0Context, request));
  };

  // Group-level action handlers - run actions using group keys instead of loading all rows
  // Current grid filters with the group keys (camelCased to match CheckByFilterRequest) on top.
  const buildGroupFilterCriteria = (groupKeys: Record<string, string>): CheckByFilterRequest => ({
    ...getCurrentFilterCriteria(),
    ...normaliseGroupKeys(groupKeys),
  });

  const onCheckIsReadyForGroup = async (groupKeys: Record<string, string>): Promise<void> => {
    await dispatch(actions.checkDependenciesByFilter(auth0Context, buildGroupFilterCriteria(groupKeys)));
    purgeGrid();
  };

  const openForceGroupDialog = (mode: ForceMode, groupKeys: Record<string, string>): void => {
    setForceGroup({ mode, criteria: buildGroupFilterCriteria(groupKeys), groupPath: toGroupPath(groupKeys) });
  };

  // Stable so the dialog's Escape listener is not re-registered on every grid render.
  const closeForceGroupDialog = useCallback(() => setForceGroup(null), []);

  const onForceGroupConfirm = async (): Promise<void> => {
    if (!forceGroup) return;
    const { mode, criteria } = forceGroup;
    setForceGroup(null);
    await dispatch(actions.forceByFilter(auth0Context, mode, criteria, false));
    purgeGrid();
  };

  const onRestoreGroupByScheme = async (groupKeys: Record<string, string>): Promise<void> => {
    const payload = [new RestorePendingDependencies(
      groupKeys.AppName || null,
      null,
      null,
      groupKeys.SchemeId || null,
      groupKeys.Type ? getRestoreTypeFromDisplayName(groupKeys.Type) : 'All',
      groupKeys.ValuationDate || null,
    )];
    await dispatch(actions.restorePendingDependencies(auth0Context, payload));
  };

  const onRestoreGroupByClient = async (groupKeys: Record<string, string>): Promise<void> => {
    const payload = [new RestorePendingDependencies(
      groupKeys.AppName || null,
      null,
      groupKeys.Client || null,
      null,
      groupKeys.Type ? getRestoreTypeFromDisplayName(groupKeys.Type) : 'All',
      groupKeys.ValuationDate || null,
    )];
    await dispatch(actions.restorePendingDependencies(auth0Context, payload));
  };

  const onRestoreGroupByClientAgent = async (groupKeys: Record<string, string>): Promise<void> => {
    const payload = [new RestorePendingDependencies(
      groupKeys.AppName || null,
      groupKeys.ClientAgent || null,
      null,
      null,
      groupKeys.Type ? getRestoreTypeFromDisplayName(groupKeys.Type) : 'All',
      groupKeys.ValuationDate || null,
    )];
    await dispatch(actions.restorePendingDependencies(auth0Context, payload));
  };

  // Helper to convert display name to restore type
  const getRestoreTypeFromDisplayName = (displayName: string): RestoreType => {
    switch (displayName) {
      case 'Liability DV': return 'LiabilityDV';
      case 'Asset DV': return 'AssetDV';
      case 'Daily Metric': return 'DailyMetric';
      default: return 'All';
    }
  };

  const onExportGroupCsvClick = async (groupKeys: Record<string, string>): Promise<void> => {
    if (!gridApi) return;
    const filterModel = gridApi.getFilterModel() ?? {};
    const columnState = gridApi.getColumnState();
    const sortModel = columnState
      .filter(c => c.sort)
      .map(c => ({ colId: c.colId, sort: c.sort }));
    const visibleColumns = columnState.filter(c => !c.hide).map(c => c.colId);
    const rowGroupColumns: Column[] = gridApi.getRowGroupColumns();
    const rowGroupCols = rowGroupColumns.map(col => ({
      id: col.getColId(),
      displayName: (col.getColDef().headerName ?? col.getColId()) as string,
      field: (col.getColDef().field ?? col.getColId()) as string,
    }));
    // Build ordered groupKeys array aligned with rowGroupCols, stopping at the deepest level present in the clicked node
    const orderedGroupKeys: string[] = [];
    for (const col of rowGroupColumns) {
      const field = col.getColDef().field ?? col.getColId();
      if (field && groupKeys[field] !== undefined) {
        orderedGroupKeys.push(groupKeys[field]);
      } else {
        break;
      }
    }
    await dispatch(actions.exportDependenciesToCsv(auth0Context, { filterModel, sortModel, rowGroupCols, groupKeys: orderedGroupKeys, visibleColumns }));
  };

  const onExportCsvClick = async (): Promise<void> => {
    if (!gridApi) return;
    const filterModel = gridApi.getFilterModel() ?? {};
    const columnState = gridApi.getColumnState();
    const sortModel = columnState
      .filter(c => c.sort)
      .map(c => ({ colId: c.colId, sort: c.sort }));
    const visibleColumns = columnState.filter(c => !c.hide).map(c => c.colId);
    await dispatch(actions.exportDependenciesToCsv(auth0Context, { filterModel, sortModel, visibleColumns }));
  };

  const onDatepickerDatesChange = (value: DateRange | undefined) => {
    const currentFilters = gridApi?.getFilterModel() ?? {};
    const fromDateMoment = moment(value?.from);
    const toDateMoment = moment(value?.to);

    const fromDateString = fromDateMoment.format("YYYY-MM-DD HH:mm:ss");
    const toDateString = toDateMoment.format("YYYY-MM-DD HH:mm:ss");

    

    if (fromDateString === toDateString) {
        if(currentFilters["ValuationDate"] &&
           currentFilters["ValuationDate"].dateFrom === fromDateString &&
           currentFilters["ValuationDate"].type === "equals"){

            return;
        }
        else {
          currentFilters["ValuationDate"] = {
            dateFrom: fromDateString,
            dateTo: null,
            filterType: "date",
            type: "equals",
          };
        }
    }
    else {
      if (currentFilters["ValuationDate"] &&
        currentFilters["ValuationDate"].dateFrom === fromDateString &&
        currentFilters["ValuationDate"].dateTo === toDateString) {

        return;
      }
      else {
        currentFilters["ValuationDate"] = {
          dateFrom: fromDateString,
          dateTo: toDateString,
          filterType: "date",
          type: "inRange",
        };
      }
    }

    gridApi?.setFilterModel(currentFilters);
    gridApi?.onFilterChanged();
  };

  return (
    <>
      <ActionsContainer>
        <ButtonsContainer>
          <GridButton size="small" variant="primary" onClick={onRefreshClick}>
            Refresh
          </GridButton>
          <GridButton size="small" variant="primary" onClick={onSaveCurrentViewClick}>
            Save Current View
          </GridButton>
          {canRelease && canRestore && (
            <>
              <GridButton size="small" variant="primary" onClick={onRestoreAllPendingDependenciesClick}>
                Restore
              </GridButton>
            </>
          )}
          {canRerun && (
            <GridButton
              size="small"
              variant="primary"
              data-testid="manual-rerun-button"
              onClick={() => setKickoffDialogOpen(true)}
            >
              Manual Rerun
            </GridButton>
          )}
        </ButtonsContainer>
        <Picker
          data-testid="date-picker"
          format="dd/mm/yyyy"
          initialResolution="day"
          mode="range"
          navigationOrdering="ascending"
          value={datepickerDates}
          onChange={onDatepickerDatesChange}
        />
      </ActionsContainer>
      <LegendRow>
        <StatusLegendButton />
      </LegendRow>

      <div
        className="ag-theme-alpine"
        style={{ height: 920, width: "100%" }}
        data-testid="dependencies-grid-container"
        data-grid-ready={gridStateApplied && gridFirstRenderDone ? "true" : "false"}
      >
        {/* @ts-ignore */}
        <AgGridReact
          columnDefs={model.columnDefinitions}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          onRowSelected={onRowSelected}
          animateRows={true}
          getChildCount={(data) => (data as any)?.childCount}
          getContextMenuItems={getContextMenuItems}
          context={{ gridApiRef }}
          components={{
            dependencyNameFilter: DependencyNameFilter,
            statusCellRenderer: StatusCellRenderer,
            statusCountsTooltip: StatusCountsTooltip,
          }}
        />
      </div>

      <DailyValuationKickoffDialog
        isOpen={kickoffDialogOpen}
        onClose={() => setKickoffDialogOpen(false)}
        onConfirm={onDailyValuationKickoffConfirm}
      />

      <ForceGroupDialog
        isOpen={forceGroup !== null}
        mode={forceGroup?.mode ?? 'Client'}
        groupPath={forceGroup?.groupPath ?? []}
        loadPreview={() => forceGroup
          ? dispatch(actions.forceByFilter(auth0Context, forceGroup.mode, forceGroup.criteria, true))
          : Promise.resolve({})}
        onConfirm={() => { void onForceGroupConfirm(); }}
        onClose={closeForceGroupDialog}
      />
    </>
  );
});
